import React from 'react';

let _sid = 0;
const nextSid = () => `sk${++_sid}`;

/* ── Full 5-petal cherry blossom with notched tips ────────────── */
const SakuraFlower = ({ size }) => {
  const id = React.useRef(nextSid()).current;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" style={{ display:'block' }}>
      <defs>
        <radialGradient id={`${id}-a`} cx="50%" cy="28%" r="72%">
          <stop offset="0%"   stopColor="#FFF8F8" />
          <stop offset="40%"  stopColor="#FFCCCC" />
          <stop offset="100%" stopColor="#FF7788" />
        </radialGradient>
        <radialGradient id={`${id}-b`} cx="50%" cy="28%" r="72%">
          <stop offset="0%"   stopColor="#FFFAFA" />
          <stop offset="40%"  stopColor="#FFC4C4" />
          <stop offset="100%" stopColor="#FF6677" />
        </radialGradient>
        <radialGradient id={`${id}-c`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#FFFAF0" />
          <stop offset="70%"  stopColor="#FFE0A0" />
          <stop offset="100%" stopColor="#FFBA50" />
        </radialGradient>
      </defs>

      {/* 5 wide petals — notched outer tip, broad oval body */}
      {[0, 72, 144, 216, 288].map((angle, i) => (
        <g key={i} transform={`rotate(${angle} 50 50)`}>
          {/*
            Petal: tip points UP (away from center at 50,50).
            Notch sits at the very top. Sides sweep wide. Base narrows toward center.
          */}
          <path
            d="M47 8
               C43 4 35 7 31 17
               C27 27 30 40 38 48
               C43 52 48 54 50 55
               C52 54 57 52 62 48
               C70 40 73 27 69 17
               C65 7 57 4 53 8
               C52 4 50 6 50 10
               C50 6 48 4 47 8 Z"
            fill={i % 2 === 0 ? `url(#${id}-a)` : `url(#${id}-b)`}
          />
        </g>
      ))}

      {/* Golden center */}
      <circle cx="50" cy="50" r="8.5" fill={`url(#${id}-c)`} />

      {/* Stamen lines */}
      {[18, 54, 90, 126, 162, 198, 234, 270, 306, 342].map((a, i) => (
        <line key={i}
          x1={50 + 8.5 * Math.cos(a * Math.PI / 180)}
          y1={50 + 8.5 * Math.sin(a * Math.PI / 180)}
          x2={50 + 14 * Math.cos(a * Math.PI / 180)}
          y2={50 + 14 * Math.sin(a * Math.PI / 180)}
          stroke="#CC2233" strokeWidth="0.9" opacity="0.55"
        />
      ))}
      {/* Stamen tips */}
      {[18, 54, 90, 126, 162, 198, 234, 270, 306, 342].map((a, i) => (
        <circle key={i}
          cx={50 + 15 * Math.cos(a * Math.PI / 180)}
          cy={50 + 15 * Math.sin(a * Math.PI / 180)}
          r="1.8" fill="#BB1122" opacity="0.70"
        />
      ))}
    </svg>
  );
};

/* ── Single fallen petal (for scattered effect) ───────────────── */
const FallenPetal = ({ size, rotate }) => {
  const id = React.useRef(nextSid()).current;
  return (
    <svg width={size} height={size * 0.65} viewBox="0 0 100 65" fill="none"
      style={{ display:'block', transform:`rotate(${rotate}deg)` }}>
      <defs>
        <radialGradient id={`${id}-fp`} cx="40%" cy="35%" r="65%">
          <stop offset="0%"   stopColor="#FFF2F2" />
          <stop offset="50%"  stopColor="#FFCCCC" />
          <stop offset="100%" stopColor="#FF7788" />
        </radialGradient>
      </defs>
      <path
        d="M8 50
           C12 30 22 10 50 4
           C78 10 88 30 92 50
           C72 62 28 62 8 50 Z"
        fill={`url(#${id}-fp)`}
        opacity="0.88"
      />
      {/* petal vein */}
      <path d="M50 4 C50 20 50 35 50 55" stroke="#DD2233" strokeWidth="0.8" opacity="0.30" />
    </svg>
  );
};

/* ── Layout — flowers spread all over the form ───────────────── */
const FLOWERS = [
  /* corners */
  { t:'-7%',  l:'-5%',  s:36, o:0.26 },
  { t:'-5%',  l:'75%',  s:30, o:0.24 },
  { t:'88%',  l:'-4%',  s:34, o:0.25 },
  { t:'86%',  l:'72%',  s:28, o:0.24 },
  /* mid edges */
  { t:'34%',  l:'-6%',  s:26, o:0.22 },
  { t:'32%',  l:'82%',  s:30, o:0.23 },
  { t:'68%',  l:'-5%',  s:24, o:0.21 },
  { t:'66%',  l:'80%',  s:28, o:0.22 },
  /* centre spread */
  { t:'8%',   l:'28%',  s:24, o:0.20 },
  { t:'10%',  l:'60%',  s:22, o:0.19 },
  { t:'25%',  l:'44%',  s:26, o:0.21 },
  { t:'44%',  l:'16%',  s:22, o:0.19 },
  { t:'46%',  l:'64%',  s:24, o:0.20 },
  { t:'60%',  l:'38%',  s:20, o:0.19 },
  { t:'74%',  l:'20%',  s:26, o:0.21 },
  { t:'72%',  l:'56%',  s:22, o:0.20 },
];

const PETALS = [
  { t:'18%',  l:'8%',   s:15, r:20,  o:0.20 },
  { t:'20%',  l:'78%',  s:13, r:-25, o:0.19 },
  { t:'38%',  l:'32%',  s:14, r:45,  o:0.20 },
  { t:'54%',  l:'52%',  s:12, r:-18, o:0.18 },
  { t:'55%',  l:'8%',   s:15, r:35,  o:0.19 },
  { t:'58%',  l:'76%',  s:13, r:-40, o:0.18 },
  { t:'78%',  l:'38%',  s:14, r:28,  o:0.20 },
  { t:'40%',  l:'72%',  s:12, r:-15, o:0.18 },
];

const SakuraFormBg = () => (
  <div aria-hidden="true" className="select-none pointer-events-none"
    style={{ position:'absolute', inset:0, overflow:'hidden', zIndex:0 }}>
    {FLOWERS.map((p, i) => (
      <div key={`f${i}`} style={{ position:'absolute', top:p.t, left:p.l, opacity:p.o }}>
        <SakuraFlower size={p.s} />
      </div>
    ))}
    {PETALS.map((p, i) => (
      <div key={`p${i}`} style={{ position:'absolute', top:p.t, left:p.l, opacity:p.o }}>
        <FallenPetal size={p.s} rotate={p.r} />
      </div>
    ))}
  </div>
);

export default SakuraFormBg;
